import React, { useMemo } from 'react'
import View from './view.js'
import CalendarDay from './CalendarDay/index'
import CalendarHeader from './CalendarHeader/index'
import { DataProvider, useDataValue } from 'Simple/Data.js'
import { currentTzDate, formatTzDate } from 'Data/format.js'
import { addDays, getDay, isBefore, subDays } from 'date-fns'

export default function Logic({ viewPath }) {
  let monthly_calendar_data = useDataValue({
    context: 'monthly_calendar_data',
    viewPath,
  })

  let selected_date = useDataValue({
    context: 'tab',
    viewPath,
    path: 'selected.date',
  })

  let optimal_date = useDataValue({
    context: 'tab',
    viewPath,
    path: 'optimal_date',
  })

  let blue_bar_range = useDataValue({
    context: 'settings',
    viewPath,
    path: 'blue_bar_range',
  })

  let time_zone_id = useDataValue({
    context: 'tab',
    viewPath,
    path: 'selected.time_zone_id',
  })

  let blueBarDays = useMemo(
    () =>
      optimal_date !== null
        ? calculateBlueBarDays(optimal_date, blue_bar_range, time_zone_id)
        : [],
    [optimal_date, blue_bar_range, time_zone_id]
  )

  function renderDay(date) {
    let formattedDate = formatTzDate(date, 'yyyy-MM-dd', time_zone_id)
    let day_data = monthly_calendar_data?.find(
      data => data?.date === formattedDate
    ) ?? {
      is_faded:
        formattedDate <
        formatTzDate(currentTzDate(time_zone_id), 'yyyy-MM-dd', time_zone_id),
    }

    return (
      <DataProvider
        context="calendar_day"
        value={{
          ...day_data,
          is_optimal: blueBarDays && blueBarDays.includes(formattedDate),
          day: formatTzDate(date, 'd', time_zone_id),
          date,
          is_selected:
            formatTzDate(selected_date, 'yyyy-MM-dd', time_zone_id) ===
            formattedDate,
        }}
        viewPath={`${viewPath}/CalendarDay(${date.getTime()})`}
      >
        <CalendarDay viewPath={`${viewPath}/CalendarDay(${date.getTime()})`} />
      </DataProvider>
    )
  }

  function weekdayElement(props) {
    return (
      <DataProvider
        context="calendar_header"
        value={{
          date: props.localeUtils
            .formatWeekdayShort(props.weekday)
            .toUpperCase(),
        }}
        viewPath={`${viewPath}/CalendarHeader`}
      >
        <CalendarHeader viewPath={`${viewPath}/CalendarHeader`} />
      </DataProvider>
    )
  }

  return (
    <View
      viewPath={viewPath}
      renderDay={renderDay}
      weekdayElement={weekdayElement}
    />
  )
}

/**
 *
 * @param {Date} optimalDate
 * @param {{ before?: number, after?: number }} blueBarRange
 * @param {string} timeZoneId
 * @returns {string[]}
 */
function calculateBlueBarDays(optimalDate, blueBarRange, timeZoneId) {
  let before = blueBarRange.before ?? 0
  let after = blueBarRange.after ?? 0

  let today = currentTzDate(timeZoneId)
  /** @type {string[]} */
  let days = []

  for (
    let i = 0, date = subDays(optimalDate, 1);
    i < before && isBefore(today, date);
    date = subDays(date, 1)
  ) {
    let day = getDay(date)
    if (day === 0 || day === 6) continue
    days.push(formatTzDate(date, 'yyyy-MM-dd', timeZoneId))
    i++
  }

  days.push(formatTzDate(optimalDate, 'yyyy-MM-dd', timeZoneId))

  for (
    let i = 0, date = addDays(optimalDate, 1);
    i < after;
    date = addDays(date, 1)
  ) {
    let day = getDay(date)
    if (day === 0 || day === 6) continue
    days.push(formatTzDate(date, 'yyyy-MM-dd', timeZoneId))
    i++
  }

  return days
}
